/**
 * Pure helpers backing the Activity panel (dev-capture surface).
 *
 * Kept framework-free so they can be unit-tested in the app's node test env
 * (mirrors `utilities/fileTree.ts`). The channel-level helpers (icon, summary,
 * filtering, SSE-event mapping) live in `@molecule/app-ide-react`; these are the
 * molecule-dev-specific bits: detail-view payload selection + the focus mapping
 * used when an inline chat card opens the panel.
 *
 * @module
 */

import type { IconName } from '@molecule/app-icons'
import type { Activity, ActivityType } from '@molecule/app-ide-react'

/**
 * The two top-level surfaces of the Activity panel: captured side effects
 * (`activity`) and brokered-service spend (`usage`).
 *
 * Activity spans BOTH environments and says which is which. A dev sandbox
 * *intercepts* a side effect (recorded, never sent); a deployed app *tees* it
 * (really sent, then recorded) — and both land in the same list, so every row
 * carries the environment that produced it and the list can be filtered by it.
 * Usage is a different resource entirely (brokered service calls), and it also
 * covers both environments — see {@link activityViewMeta}.
 */
export type ActivityView = 'activity' | 'usage'

/** The environment a captured row was produced in. */
export type ActivityScope = 'dev' | 'prod'

/** The environment filter the Activity list can be narrowed to. */
export type ActivityScopeFilter = ActivityScope | null

/**
 * A captured activity as the molecule-dev list endpoint returns it: the shared
 * `Activity` contract plus the two columns only this platform has.
 *
 * Declared here rather than widening `@molecule/app-ide-react`'s shared type,
 * because these two fields are molecule-dev's control-plane bookkeeping, not
 * part of the IDE component contract every scaffolded app compiles against.
 */
export interface CapturedActivity extends Activity {
  /** Which environment produced it; null on rows captured before this existed. */
  environment?: ActivityScope | null
  /** When it was re-sent to the account owner's inbox, else null. */
  inboxSentAt?: string | null
}

/**
 * Maps a channel type to a glyph name in the bonded icon set (`@molecule/app-icons`
 * — the molecule set plus the workspace extras in `bonds/workspace-icons.ts`), so
 * the Activity panel renders the same themed SVG `<Icon>` family as the rest of
 * the IDE instead of raw emoji. Every returned name MUST resolve via `getIcon`
 * (which throws on an unknown name); `bonds/workspace-icons.ts` supplies the two
 * channel glyphs the base set lacks (`chat` for SMS, `hash` for channel).
 * @param type - The activity channel type.
 * @returns The icon-set name to pass to `<Icon name=… />`.
 */
export function activityIconName(type: ActivityType): IconName {
  switch (type) {
    case 'email':
      return 'mail'
    case 'sms':
      return 'chat'
    case 'push':
      return 'bell'
    case 'webhook':
      return 'link'
    case 'channel':
      return 'hash'
    default:
      // Unknown/future channel types (activityFromEvent defaults them to
      // `webhook`) reuse the webhook glyph rather than risk a getIcon throw.
      return 'link'
  }
}

/**
 * Maps an {@link ActivityView} to the environment(s) it reflects.
 *
 * Both views are `both`: captured Activity now carries prod rows from deployed
 * apps that tee their real sends, and brokered Usage has never had an
 * environment predicate at all — a dev sandbox's brokered AI call is metered
 * exactly like a deployed app's. The panel used to caption Activity "Captured
 * from your dev sandbox" and Usage "from your deployed app"; both were wrong in
 * the same direction, and on a billing surface the second is the worse one.
 *
 * @param view - The active panel view.
 * @returns The environment scope the view's data actually spans.
 */
export function activityViewMeta(view: ActivityView): { scope: 'both' } {
  // Kept as a function (and still parameterised) so the panel has ONE place to
  // ask "what does this view cover?" if the surfaces ever diverge again.
  void view
  return { scope: 'both' }
}

/**
 * Label for a row's environment badge, or null when the row predates
 * environment stamping — an unknown environment is shown as nothing rather
 * than guessed as `dev`.
 *
 * @param environment - The row's environment.
 * @returns `'Dev'` / `'Production'`, or null when unknown.
 */
export function activityScopeLabel(environment: ActivityScope | null | undefined): string | null {
  if (environment === 'dev') return 'Dev'
  if (environment === 'prod') return 'Production'
  return null
}

/** The truncation notice key the API attaches to a payload it had to cut. */
export const ACTIVITY_TRUNCATION_KEY = '_moleculeTruncated'

/** What the API cut from an oversized captured payload. */
export interface ActivityTruncation {
  originalBytes: number
  truncatedFields: string[]
  droppedFields: string[]
}

/**
 * Read the API's truncation notice off a captured payload/result.
 *
 * An oversized payload used to be DROPPED (stored null), so the expanded detail
 * rendered as an empty box with nothing to explain it — a 90 KB HTML email, an
 * ordinary transactional template, simply vanished. It is now cut down with a
 * notice, and this reads that notice so the panel can say what happened.
 *
 * @param value - A captured payload or result.
 * @returns The notice, or null when nothing was cut.
 */
export function activityTruncation(value: unknown): ActivityTruncation | null {
  if (!value || typeof value !== 'object') return null
  const notice = (value as Record<string, unknown>)[ACTIVITY_TRUNCATION_KEY]
  if (!notice || typeof notice !== 'object') return null
  const n = notice as Partial<ActivityTruncation>
  return {
    originalBytes: typeof n.originalBytes === 'number' ? n.originalBytes : 0,
    truncatedFields: Array.isArray(n.truncatedFields) ? n.truncatedFields : [],
    droppedFields: Array.isArray(n.droppedFields) ? n.droppedFields : [],
  }
}

/**
 * Human byte size for the truncation notice ("94 KB").
 * @param bytes - A byte count.
 * @returns A short human label.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  if (bytes < 1024) return `${Math.round(bytes)} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Short relative age for a row's timestamp ("just now", "5m ago", "3d ago").
 *
 * The list rendered no date or time at all, so a capture from five minutes ago
 * and one from last week were distinguishable only by their position.
 *
 * @param timestamp - The row's ISO 8601 timestamp.
 * @param nowMs - "Now" in epoch ms (injectable so this is testable).
 * @returns A short relative label, or null when the timestamp is unusable.
 */
export function formatActivityAge(timestamp: string | undefined, nowMs: number): string | null {
  if (!timestamp) return null
  const then = new Date(timestamp).getTime()
  if (Number.isNaN(then)) return null
  const seconds = Math.max(0, Math.round((nowMs - then) / 1000))
  if (seconds < 45) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(then).toLocaleDateString()
}

/**
 * Full local timestamp for the row's `title`, so the exact instant is one hover
 * away from the relative label.
 *
 * @param timestamp - The row's ISO 8601 timestamp.
 * @returns The localized date+time, or null when unusable.
 */
export function formatActivityTimestamp(timestamp: string | undefined): string | null {
  if (!timestamp) return null
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString()
}

/**
 * Merge freshly-polled rows into the list the panel already holds, newest
 * first, without duplicating a row that arrived twice.
 *
 * The poll asks for `?since=<newest row's timestamp>`, so the steady-state
 * response is an empty array and the whole list no longer crosses the wire
 * every four seconds.
 *
 * @param existing - The rows already rendered.
 * @param incoming - The rows the poll returned.
 * @returns The merged list, newest first.
 */
export function mergeActivities<T extends { id: string; timestamp: string }>(
  existing: readonly T[],
  incoming: readonly T[],
): T[] {
  if (incoming.length === 0) return existing as T[]
  const byId = new Map<string, T>()
  for (const row of existing) byId.set(row.id, row)
  for (const row of incoming) byId.set(row.id, row)
  return [...byId.values()].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  )
}

/**
 * Which channels the project can produce is derived SERVER-side now, from
 * `projects.packages` in the platform DB, and arrives on the list response as
 * `wiredTypes` (see `App/Resource/Activity/service.ts`). The panel used to work
 * it out here from `GET /projects/:id/services`, which connects to the sandbox,
 * reads two package.json files and runs a recursive grep — a full round trip
 * (and, on a paused sandbox, a RESUME) to choose between two empty-state
 * sentences. One derivation, in the place that already holds the data.
 */

/**
 * The empty-state flavor the Activity panel should render for the current
 * filter tab, given what's wired:
 *
 *  - `'wired'` — the channel(s) can produce activity; show a "trigger one" hint.
 *  - `'unwired'` — the app has no provider for the channel(s); say so plainly.
 *  - `'unknown'` — package list unavailable; keep the generic empty copy.
 *
 * The "All" tab (null filter) counts as wired when ANY channel is wired.
 * @param activeType - The selected channel filter (null = All).
 * @param wired - The wired-channel set, or null while unknown.
 * @returns The empty-state flavor.
 */
export function activityEmptyStateKind(
  activeType: ActivityType | null,
  wired: ReadonlySet<ActivityType> | null,
): 'wired' | 'unwired' | 'unknown' {
  if (!wired) return 'unknown'
  if (activeType === null) return wired.size > 0 ? 'wired' : 'unwired'
  return wired.has(activeType) ? 'wired' : 'unwired'
}

/** Focus state applied to the Activity panel when opened from an inline card. */
export interface ActivityFocus {
  /** Channel-filter tab to pre-select (null = "All"). */
  type: ActivityType | null
  /** Activity row to auto-expand. */
  id: string | null
}

/**
 * Derives the panel focus (filter tab + expanded row) from a clicked inline card.
 * @param activity - The activity whose card was clicked.
 * @returns The focus state to apply to the Activity panel.
 */
export function focusFromActivity(activity: Pick<Activity, 'type' | 'id'>): ActivityFocus {
  return { type: activity.type, id: activity.id }
}

/** The detail-rendering mode the panel uses for an activity's expanded view. */
export type DetailMode = 'email-html' | 'email-text' | 'message-body' | 'json'

/**
 * Decides how an activity's expanded detail should be rendered, given its
 * captured payload. Email prefers rendered HTML, then text; SMS/channel prefer
 * the message body; everything else (and missing fields) falls back to JSON.
 * @param activity - The activity to inspect.
 * @returns The detail-rendering mode.
 */
export function detailMode(activity: Activity): DetailMode {
  const payload = activity.payload as Record<string, unknown> | undefined
  if (activity.type === 'email' && payload) {
    if (typeof payload.html === 'string') return 'email-html'
    if (typeof payload.text === 'string') return 'email-text'
    return 'json'
  }
  if ((activity.type === 'sms' || activity.type === 'channel') && payload) {
    if (typeof payload.message === 'string' || typeof payload.body === 'string') {
      return 'message-body'
    }
  }
  return 'json'
}

/**
 * Extracts the structured `{ error, errorKey }` envelope a failed activity
 * request carries on its thrown HTTP error (the molecule-dev API returns this
 * shape on every 4xx/5xx — see `App/Resource/Activity/index.ts`). Framework-free
 * so the panel can map a `send-to-inbox` failure to a localized, actionable
 * message instead of swallowing it silently.
 * @param err - The thrown HTTP error.
 * @returns The `errorKey` (e.g. `activity.error.noEmailTransport`) and human
 *   `message` if present on the response body, else `null` for each.
 */
export function activityRequestError(err: unknown): {
  message: string | null
  errorKey: string | null
} {
  const data = (err as { response?: { data?: { error?: string; errorKey?: string } } })?.response
    ?.data
  return {
    message: typeof data?.error === 'string' ? data.error : null,
    errorKey: typeof data?.errorKey === 'string' ? data.errorKey : null,
  }
}

/**
 * Pretty-prints an unknown payload/result value as JSON for the detail view.
 * @param value - The value to stringify.
 * @returns A formatted JSON string, the raw string if already a string, or '' for nullish.
 */
export function formatPayload(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch (_error) {
    // Best-effort fallback: JSON.stringify can throw for circular refs or
    // non-serialisable types (BigInt etc.); String() is always safe here.
    return String(value)
  }
}
